import requiredError from 'components/@home/@settings/Tabs/requiredError';
import { withFormik } from 'formik';
import { I18n } from 'utils/i18n';
import Yup from 'utils/CustomYup';

class FormSubmit {
  constructor(values, formikBag) {
    this.formikBag = formikBag;
    this.values = values;
  }

  assembleForm = () => {
    const {
      values: { name, type, enabled, configuration, failureNotificationEmails },
    } = this;
    return { name, type, enabled, configuration, failureNotificationEmails };
  };

  sendRequest = async form => {
    let ret;
    const { integration: { _id } = { _id: 'new' }, actions } = this.formikBag.props;
    if (_id === 'new') {
      const newForm = { ...form };
      delete newForm._id;
      ret = await actions.chatFlowIntegrations.create(newForm);
      actions.chatFlows.load();
    } else {
      ret = await actions.chatFlowIntegrations.update(_id, form);
      actions.chatFlows.load();
    }
    ret = await actions.chatFlowIntegrations.find(ret.value._id);
    return ret.value;
  };

  displayErrors = () => {
    const { setErrors } = this.formikBag;
    setErrors({ server: I18n.t('Something went wrong with sending data') });
  };
}

const getValidationSchemaForType = type => {
  const baseSchema = {
    name: Yup.string().required(requiredError('name')),
    type: Yup.string()
      .required(requiredError('type'))
      .oneOf(['google_sheets', 'excel', 'slack', 'email', 'zapier', 'custom_webhook']),
    enabled: Yup.boolean(),
    failureNotificationEmails: Yup.array().of(Yup.string().email()),
  };

  const requiredWhenCloudConnected = field =>
    Yup.string().when('tokenId', {
      is: tokenId => Boolean(tokenId),
      then: schema => schema.required(requiredError(field)),
    });

  const configurationSchemas = {
    google_sheets: Yup.object({
      tokenId: Yup.string(),
      spreadsheetId: requiredWhenCloudConnected('spreadsheetId'),
      sheetName: requiredWhenCloudConnected('sheetName'),
      range: Yup.string(),
      userEmail: Yup.string().email(),
      selectedFileName: Yup.string(),
    }),
    excel: Yup.object({
      tokenId: Yup.string(),
      driveId: requiredWhenCloudConnected('driveId'),
      fileId: requiredWhenCloudConnected('fileId'),
      worksheetName: requiredWhenCloudConnected('worksheetName'),
      userEmail: Yup.string().email(),
      selectedFileName: Yup.string(),
      sharePointUrl: Yup.string(),
    }),
    slack: Yup.object({
      webhookUrl: Yup.string().url().required(requiredError('webhookUrl')),
      channel: Yup.string(),
    }),
    email: Yup.object({
      recipients: Yup.array()
        .of(Yup.string().email())
        .min(1, 'At least one recipient is required')
        .required(requiredError('recipients')),
      subject: Yup.string(),
      template: Yup.string(),
    }),
    zapier: Yup.object({
      zapierWebhookUrl: Yup.string().url().required(requiredError('zapierWebhookUrl')),
    }),
    custom_webhook: Yup.object({
      url: Yup.string().url().required(requiredError('url')),
      timeout: Yup.number().min(1).max(30),
      authentication: Yup.object({
        username: Yup.string(),
        password: Yup.string(),
      }),
      headers: Yup.array().of(
        Yup.object({
          key: Yup.string()
            .matches(/^[a-z][a-z0-9_-]*$/i, () => I18n.t('ChatFlowSettings.errors.Variable name'))
            .required(requiredError('key')),
          value: Yup.string().required(requiredError('value')),
        }),
      ),
    }),
  };

  if (type && configurationSchemas[type]) {
    baseSchema.configuration = configurationSchemas[type];
  } else {
    baseSchema.configuration = Yup.object();
  }

  return Yup.object().shape(baseSchema);
};

const chatFlowIntegrationFormik = withFormik({
  enableReinitialize: true,
  validateOnChange: true,
  validationSchema: ({ integration }) => {
    const type = integration?.type;
    return getValidationSchemaForType(type);
  },
  mapPropsToValues: ({ integration }) => {
    // Ensure configuration has proper default values based on integration type
    const getDefaultConfiguration = type => {
      const defaults = {
        email: {
          recipients: [],
          subject: '',
          template: '',
        },
        slack: {
          webhookUrl: '',
          channel: '',
        },
        google_sheets: {
          spreadsheetId: '',
          sheetName: '',
          range: '',
          tokenId: '',
          userEmail: '',
          selectedFileName: '',
        },
        excel: {
          tokenId: '',
          driveId: '',
          fileId: '',
          worksheetName: '',
          selectedFileName: '',
          userEmail: '',
          sharePointUrl: '',
        },
        zapier: {
          zapierWebhookUrl: '',
        },
        custom_webhook: {
          url: '',
          timeout: 10,
          authentication: {
            username: '',
            password: '',
          },
          headers: [],
        },
      };
      return defaults[type] || {};
    };

    const defaultConfig = getDefaultConfiguration(integration?.type);
    const mergedConfig = { ...defaultConfig, ...integration?.configuration };

    return {
      _id: integration?._id || 'new',
      name: integration?.name || '',
      type: integration?.type || '',
      enabled: integration?.enabled !== false,
      configuration: mergedConfig,
      failureNotificationEmails: integration?.failureNotificationEmails || [],
    };
  },
  handleSubmit: async (values, formikBag) => {
    const {
      props: { context },
      setSubmitting,
      resetForm,
    } = formikBag;

    const handler = new FormSubmit(values, formikBag);
    const form = handler.assembleForm();

    try {
      const ret = await handler.sendRequest(form);
      if (context.current.callback) {
        context.current.callback(ret);
      }
      context.pop();
      resetForm();
    } catch (err) {
      setSubmitting(false);
      handler.displayErrors(err);
    }
    return true;
  },
});

export default chatFlowIntegrationFormik;
